// The program panel: write jax, pick a slice, simulate it. It takes over the main area
// the way the welcome page does, and on a finished run it navigates to that run.
//
// The editor itself is the one module that exists only bundled (bare imports of
// CodeMirror); it is fetched from dist/ the first time the panel opens, so a page that
// never writes a program never pays for it.

import { $, el, esc, debounce } from "./dom.js";
import { api } from "./api.js";
import { describe } from "./failure.js";

const SAVED = "playground.program";       // survives a reload, and the trip to a run
const SAVED_TOPOLOGY = "playground.topology";
const SAVED_JOB = "playground.job";       // the job to pick back up if the tab reloads
const START = `import jax
import jax.numpy as jnp

@jax.jit
def program(x):
    return jnp.tanh(x @ x.T)

print(jax.devices())
print(program(jnp.ones((256, 256))).sum())
`;

function remember(key, value) {
  try { localStorage.setItem(key, value); } catch (_) { /* private window: not worth a word */ }
}

function recall(key) {
  try { return localStorage.getItem(key); } catch (_) { return null; }
}

function forget(key) {
  try { localStorage.removeItem(key); } catch (_) { /* nothing to undo */ }
}

export function openProgram(root, examples) {
  root.textContent = "";
  root.classList.remove("welcome");
  const panel = el("section", "program");
  panel.innerHTML = `
    <div class="prog-bar">
      <label>slice <select id="prog-topology"></select></label>
      <label>start from <select id="prog-start"><option value="">an empty program</option></select></label>
      <button id="prog-run" class="btn">simulate</button>
      <span id="prog-status" class="dim"></span>
    </div>
    <div id="prog-editor" class="panel"></div>
    <div id="prog-out"></div>`;
  root.appendChild(panel);

  const topologies = $("#prog-topology", panel);
  const start = $("#prog-start", panel);
  const status = $("#prog-status", panel);
  const out = $("#prog-out", panel);

  for (const ex of examples || []) {
    if (ex.editor === false) continue;
    const o = el("option", null, `${ex.title} (${ex.topology})`);
    o.value = ex.key;
    o.dataset.topology = ex.topology;
    start.appendChild(o);
  }

  fillTopologies(topologies).catch((e) => { status.textContent = "no topologies: " + e.message; });

  let view = null;
  const editorHost = $("#prog-editor", panel);
  loadEditor().then((createEditor) => {
    view = createEditor(editorHost, {
      doc: recall(SAVED) ?? START,
      onChange: debounce((text) => remember(SAVED, text), 400),
      onSubmit: () => simulate(),
    });
    window.__editor = view;    // the headless test writes the program through this
    view.focus();
  }, (e) => {
    editorHost.textContent = "";
    editorHost.appendChild(el("p", "dim", `the editor is served built only: run \`cd viewer && npm run build\` (${e.message})`));
  });

  // A click on one of the program's own frames in a traceback moves the cursor there,
  // the way an IDE's own stack trace does. Guarded for a line a since-edited doc no
  // longer has.
  function jumpTo(n) {
    if (!view || !Number.isFinite(n)) return;
    const doc = view.state.doc;
    if (n < 1 || n > doc.lines) return;
    view.dispatch({ selection: { anchor: doc.line(n).from }, scrollIntoView: true });
    view.focus();
  }

  start.onchange = async () => {
    const key = start.value;
    if (!key || !view) return;
    const picked = start.selectedOptions[0].dataset.topology;
    if ([...topologies.options].some((o) => o.value === picked)) topologies.value = picked;
    // An example ships with the page (`runs/` beside it) unless the export left it at
    // the API, in which case its entry says where.
    const ex = (examples || []).find((e) => e.key === key);
    const r = await fetch(new URL(`${ex && ex.url ? ex.url : "runs/" + key + "/"}program.py`, location.href));
    if (!r.ok) { status.textContent = "that example has no program"; return; }
    setDoc(view, await r.text());
  };

  topologies.onchange = () => remember(SAVED_TOPOLOGY, topologies.value);

  const button = $("#prog-run", panel);
  button.onclick = () => simulate();
  let inFlight = false;

  async function simulate() {
    if (inFlight || !view) return;
    out.textContent = "";
    const program = view.state.doc.toString();
    remember(SAVED, program);
    begin();
    try {
      const r = await fetch(api("api/submit"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ program, topology: topologies.value }),
      });
      if (!r.ok) {
        // Check r.ok before parsing: a 429/503 from the rate limiter or a proxy may
        // not even be JSON.
        let body;
        try { body = await r.json(); } catch (_) { body = {}; }
        body.error = body.error || `the server said ${r.status}`;
        body.http = r.status;
        const info = describe(body, null, topologies.value);
        end(info.headline, true);
        report(out, body, { topology: topologies.value, jumpTo });
        return;
      }
      const job = await r.json();
      remember(SAVED_JOB, job.id);          // a reload mid-run picks the job back up
      await follow(job);
    } catch (e) {
      const message = "the server did not answer: " + e.message;
      end(message, true);
      report(out, { error: message, http: 0, submit: true }, { jumpTo });
    }
  }

  // Every job ends the same way, whether it was just submitted or found in
  // localStorage after a reload: poll until it is done, saying where it is meanwhile.
  // The poll is also the lease -- a page that stops asking has its job cancelled.
  async function follow(job) {
    begin();
    while (job && job.state !== "done" && job.state !== "cancelled") {
      show(job);
      await new Promise((r) => setTimeout(r, 600));
      const r = await fetch(api(`api/jobs/${job.id}`));
      if (!r.ok) { forget(SAVED_JOB); end("the job is gone; submit it again", true); return; }
      job = await r.json();
    }
    forget(SAVED_JOB);
    if (job.state === "cancelled") { end("cancelled: the page that submitted it went away"); return; }
    const answer = job.result || {};
    if (answer.status === "ok") {
      end(`done in ${job.elapsed_s} s, opening the run...`);
      location.href = `?run=${answer.key}`;   // wherever it is: js/load.js finds it
      return;
    }
    // The block below already says what happened; the status span only needs a short
    // echo of it (`answer.status`, since a timeout and an error read differently).
    const said = { timeout: "timeout", oom: "out of memory", no_trace: "no trace" }[answer.status] || "failed";
    end(`${said} after ${job.elapsed_s} s`, true);
    report(out, answer, { job, topology: topologies.value, jumpTo });
  }

  function show(job) {
    if (job.state !== "queued") { status.textContent = `running... ${Math.round(job.elapsed_s)} s`; return; }
    let text = job.ahead === 0 ? "queued, next" : `queued, ${job.ahead} ahead`;
    if (job.in_use + job.weight > job.capacity) text += " (waiting for room)";
    status.textContent = text;
  }

  function begin() {
    inFlight = true;
    button.disabled = true;
    status.classList.remove("bad");
    status.textContent = status.textContent || "submitting...";
  }

  function end(text, bad) {
    inFlight = false;
    button.disabled = false;
    status.textContent = text;
    status.classList.toggle("bad", !!bad);
  }

  const waiting = recall(SAVED_JOB);
  if (waiting) {
    status.textContent = "picking your run back up...";
    fetch(api(`api/jobs/${waiting}`))
      .then((r) => (r.ok ? r.json() : null))
      .then((job) => (job ? follow(job) : (forget(SAVED_JOB), (status.textContent = ""))))
      .catch(() => { forget(SAVED_JOB); end(""); });   // don't leave the button disabled
  }

  return { simulate };
}

// What a failed run says: `failure.describe` decides what it means, this renders it.
// `extra` carries what `describe` needs beyond the answer itself (`job`, for its
// elapsed time and queue position; `topology`, the slice picked in the bar) and
// `jumpTo`, called with a line number when the reader clicks one of their own frames.
function report(out, answer, extra = {}) {
  out.textContent = "";
  const info = describe(answer, extra.job, extra.topology);
  const block = el("div", "prog-fail");
  block.dataset.tone = info.tone;
  if (info.headline) block.appendChild(el("p", "fail-head", info.headline));
  if (info.detail) block.appendChild(el("p", "dim", info.detail));
  if (info.hint) block.appendChild(el("p", "fail-hint", info.hint));
  out.appendChild(block);

  if (info.traceback) {
    const box = el("div", "prog-stream");
    const pre = el("pre", "mono");
    pre.innerHTML = tracebackHTML(info.traceback, info.headline);
    pre.addEventListener("click", (e) => {
      const btn = e.target.closest("button[data-line]");
      if (btn && extra.jumpTo) extra.jumpTo(Number(btn.dataset.line));
    });
    box.appendChild(pre);
    out.appendChild(box);
  }

  if (info.stdout) {
    const lines = info.stdout.replace(/\n$/, "").split("\n").length;
    const details = el("details", "prog-stream");
    details.open = !!info.stdoutOpen;     // a run without a trace: its output is the result
    details.appendChild(el("summary", "dim", `stdout, ${lines} line${lines === 1 ? "" : "s"}`));
    details.appendChild(el("pre", "mono", info.stdout));
    out.appendChild(details);
  }

  out.scrollIntoView({ block: "nearest" });
}

// The traceback, line by line: the reader's own frames become a button that jumps the
// editor there, and the exception line itself is picked out -- everything else is
// plain text, `esc`aped, never raw (this is going through innerHTML for the spans).
const OWN_FRAME = /File "(?:\/launch\/)?program\.py", line (\d+)/;
function tracebackHTML(text, headline) {
  return text.split("\n").map((line) => {
    const frame = line.match(OWN_FRAME);
    if (frame) return `<span class="own"><button type="button" class="linkish" data-line="${frame[1]}">${esc(line)}</button></span>`;
    if (headline && line.trim() === headline.trim()) return `<span class="fail-line">${esc(line)}</span>`;
    return esc(line);
  }).join("\n");
}

async function fillTopologies(select) {
  const r = await fetch(api("api/topologies"));
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const { topologies } = await r.json();
  let group = null;
  for (const t of topologies) {
    if (!group || group.label !== t.gen) {
      group = el("optgroup");
      group.label = t.gen;
      select.appendChild(group);
    }
    const cores = `${t.chips} chip${t.chips === 1 ? "" : "s"}, ${t.tensorcores} TC` +
      (t.sparsecores ? `, ${t.sparsecores} SC` : "");
    const o = el("option", null, `${t.key} - ${cores}`);
    o.value = t.key;
    group.appendChild(o);
  }
  const was = recall(SAVED_TOPOLOGY);
  if (was && [...select.options].some((o) => o.value === was)) select.value = was;
}

function setDoc(view, text) {
  view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
  remember(SAVED, text);
}

async function loadEditor() {
  const mod = await import(new URL("../dist/editor.js", import.meta.url).href);
  return mod.createEditor;
}
